import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState } from "@/components/Kpi";
import { Badge, CAT_LABEL, Impatto, LinkFonte, NewsCard, dataOra } from "@/components/NewsCard";

const Etichetta = ({ children, tono = "fonte" }) => (
  <span className={`text-[10px] font-bold tracking-[0.16em] uppercase px-1.5 py-0.5 rounded
    ${tono === "fonte" ? "bg-slate-900 text-white" : "bg-amber-500 text-white"}`}>
    {children}
  </span>
);

export default function NewsArticolo() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api.get(`/news/articolo/${slug}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [slug]);

  if (d === undefined) {
    return <Card className="p-10 text-center text-sm text-slate-500">Caricamento notizia…</Card>;
  }
  if (!d) {
    return (
      <EmptyState title="Notizia non trovata"
        description="Il contenuto potrebbe non essere più disponibile in archivio." />
    );
  }
  const a = d.articolo;
  const istituzionale = a.fonte_tipo === "istituzionale";

  return (
    <div data-testid="news-articolo" className="max-w-4xl">
      <button onClick={() => navigate(-1)} data-testid="news-indietro"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900
          transition-colors duration-200 mb-4">
        <ArrowLeft size={14} /> Torna alle news
      </button>

      <Card className="p-6">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold txt-caricato">
            {CAT_LABEL[a.categoria] || a.categoria}
          </span>
          {(a.badge || []).map((b) => <Badge key={b} b={b} />)}
          <Impatto v={a.impatto} />
        </div>
        <h1 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 leading-tight"
          data-testid="articolo-titolo">
          {a.titolo}
        </h1>
        <div className="mt-3 text-sm text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{dataOra(a.pubblicato_il)}</span>
          <span className="text-slate-300">·</span>
          <span className="font-medium text-slate-700">{a.editore ? `${a.editore} · via ${a.fonte_nome}` : a.fonte_nome}</span>
          <span className="text-[10px] uppercase tracking-wider border border-slate-200 rounded px-1.5 py-0.5">
            {istituzionale ? "Fonte istituzionale" : a.fonte_tipo === "aziendale"
              ? "Fonte aziendale / Comunicato stampa" : "Fonte editoriale"}
          </span>
          <span className="font-mono text-slate-400">rilevanza {a.relevance}</span>
        </div>
        {a.paywall && (
          <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2
            inline-flex items-center gap-2" data-testid="articolo-paywall">
            <Lock size={14} /> Contenuto disponibile sulla fonte originale
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Etichetta>Dalla fonte</Etichetta>
            <span className="text-xs text-slate-400">sintesi generata dal sistema sui contenuti pubblici</span>
          </div>
          <p className="text-slate-700 leading-relaxed" data-testid="articolo-sintesi">
            {a.sintesi || a.sommario_fonte
              || "Sintesi non ancora disponibile: la notizia è in lavorazione."}
          </p>
        </div>

        {(a.punti_chiave || []).length > 0 && (
          <div className="mt-6" data-testid="articolo-punti">
            <h2 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-2">
              Punti chiave
            </h2>
            <ul className="space-y-1.5">
              {a.punti_chiave.map((p, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="txt-caricato font-bold">·</span><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(a.dati || []).length > 0 && (
          <div className="mt-6" data-testid="articolo-dati">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
                Dati principali
              </h2>
              <Etichetta>Dalla fonte</Etichetta>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Indicatore</th>
                    <th className="text-left px-4 py-2">Valore</th>
                    <th className="text-left px-4 py-2">Periodo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {a.dati.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-slate-700">{r.indicatore || "N/D"}</td>
                      <td className="px-4 py-2 font-mono font-semibold txt-caricato">{r.valore || "N/D"}</td>
                      <td className="px-4 py-2 text-slate-500">{r.periodo || "N/D"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(a.perche_importante || a.impatto_mercato) && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50/50 p-4" data-testid="articolo-analisi">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-600">
                Impatto sul mercato
              </h2>
              <Etichetta tono="analisi">Analisi</Etichetta>
            </div>
            {a.perche_importante && (
              <p className="text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold">Perché è importante · </span>{a.perche_importante}
              </p>
            )}
            {a.impatto_mercato && (
              <p className="text-sm text-slate-700 leading-relaxed mt-2">{a.impatto_mercato}</p>
            )}
            <p className="text-[11px] text-slate-500 mt-3">
              Interpretazione generata dal sistema, non riportata dalla fonte.
            </p>
          </div>
        )}

        <div className="mt-8 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-500">
            <div>Fonte: <span className="font-medium text-slate-700">{a.editore ? `${a.editore} · via ${a.fonte_nome}` : a.fonte_nome}</span></div>
            <div>Pubblicato: {dataOra(a.pubblicato_il)}</div>
            <div className="text-xs text-slate-400">Acquisito: {dataOra(a.acquisito_il)}</div>
          </div>
          <LinkFonte url={a.url} testId="articolo-fonte-originale" />
        </div>
      </Card>

      {(d.correlati || []).length > 0 && (
        <div className="mt-8">
          <h2 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
            Notizie correlate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="articolo-correlati">
            {d.correlati.map((c) => <NewsCard key={c.slug} a={c} compact />)}
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link to="/news" className="text-sm text-sky-700 hover:text-sky-900 transition-colors duration-200">
          ← Tutte le news
        </Link>
      </div>
    </div>
  );
}
